import { useState } from 'react';
import { leadsAPI } from '../../lib/api';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Download, Upload } from 'lucide-react';

const SOURCES = ['BROKERBIN', 'LINKEDIN', 'WEBSITE_CHAT', 'WEBSITE_VISIT', 'WEB_RESEARCH', 'GOOGLE_ADS', 'FACEBOOK', 'REFERRAL', 'TRADE_SHOW', 'OTHER'];
const RESULT_COLORS = { NEW: 'bg-emerald-100 text-emerald-800', FLAGGED: 'bg-amber-100 text-amber-800', DUPLICATE: 'bg-red-100 text-red-800', ERROR: 'bg-gray-200 text-gray-700' };

export default function UploadTab({ onDone }) {
  const [file, setFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [leadSource, setLeadSource] = useState('OTHER');
  const [batchName, setBatchName] = useState('');
  const [leadType, setLeadType] = useState('CUSTOMER');
  const [preview, setPreview] = useState(null);
  const [keepFlagged, setKeepFlagged] = useState(true);
  const [resultFilter, setResultFilter] = useState('ALL');
  const [loading, setLoading] = useState(false);

  const downloadTemplate = async () => {
    try {
      const { data } = await leadsAPI.getTemplate();
      const link = document.createElement('a');
      link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${data.excel_base64}`;
      link.download = data.filename;
      link.click();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to download template');
    }
  };

  const doPreview = async () => {
    if (!file) { toast.error('Choose a file first'); return; }
    setLoading(true);
    try {
      const { data } = await leadsAPI.uploadPreview(file, { lead_source: leadSource, batch_name: batchName, lead_type: leadType, owner_mode: 'SINGLE' });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally { setLoading(false); }
  };

  const doConfirm = async () => {
    setLoading(true);
    try {
      const { data } = await leadsAPI.uploadConfirm(preview.preview_id, keepFlagged);
      toast.success(`Created ${data.created} lead(s), ${data.kept_flagged} flagged kept, ${data.rejected} rejected`);
      setPreview(null); setFile(null); setFileInputKey(k => k + 1);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Confirm failed');
    } finally { setLoading(false); }
  };

  const filteredRows = preview ? preview.rows.filter(r => resultFilter === 'ALL' || r.result === resultFilter) : [];

  return (
    <div className="space-y-4 mt-4" data-testid="leads-upload-tab">
      <Card className="p-4 space-y-3 max-w-xl">
        <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="leads-download-template-btn"><Download className="h-4 w-4 mr-1" /> Download template</Button>
        <div>
          <Label>File (XLSX/XLS/CSV)</Label>
          <Input type="file" key={fileInputKey} accept=".xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files[0])} data-testid="leads-upload-file-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Lead source</Label>
            <Select value={leadSource} onValueChange={setLeadSource}>
              <SelectTrigger className="h-9" data-testid="leads-upload-source-select"><SelectValue /></SelectTrigger>
              <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Default lead type</Label>
            <Select value={leadType} onValueChange={setLeadType}>
              <SelectTrigger className="h-9" data-testid="leads-upload-type-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="CUSTOMER">Customer</SelectItem><SelectItem value="VENDOR">Vendor</SelectItem><SelectItem value="BOTH">Both</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Batch name</Label>
          <Input value={batchName} onChange={(e) => setBatchName(e.target.value)} placeholder="e.g. BrokerBin scrape Nov" data-testid="leads-upload-batch-name" />
        </div>
        <Button onClick={doPreview} disabled={loading} data-testid="leads-upload-preview-btn"><Upload className="h-4 w-4 mr-1" /> Preview</Button>
      </Card>

      {preview && (
        <Card className="p-4 space-y-3" data-testid="leads-upload-preview-card">
          <div className="flex gap-2 flex-wrap">
            {Object.entries(preview.counts).map(([k, v]) => <Badge key={k} className={RESULT_COLORS[k] || ''} onClick={() => setResultFilter(k)} data-testid={`leads-preview-count-${k}`}>{k}: {v}</Badge>)}
            <Badge variant="outline" className="cursor-pointer" onClick={() => setResultFilter('ALL')}>Show all</Badge>
          </div>
          <div className="max-h-96 overflow-y-auto border rounded-sm divide-y">
            {filteredRows.map(r => (
              <div key={r.row} className="px-3 py-2 text-sm flex items-center justify-between" data-testid={`leads-preview-row-${r.row}`}>
                <div className="min-w-0 flex-1 truncate">Row {r.row}: {r.cleaned.company} — {r.cleaned.email || r.cleaned.phone}</div>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <Badge className={RESULT_COLORS[r.result]}>{r.result}</Badge>
                  {r.reasons.length > 0 && <span className="text-xs text-muted-foreground max-w-xs truncate">{r.reasons.join('; ')}</span>}
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="keep-flagged" checked={keepFlagged} onCheckedChange={setKeepFlagged} data-testid="leads-keep-flagged-checkbox" />
            <Label htmlFor="keep-flagged">Keep flagged rows (add anyway)</Label>
          </div>
          <Button onClick={doConfirm} disabled={loading} data-testid="leads-upload-confirm-btn">Confirm &amp; create leads</Button>
        </Card>
      )}
    </div>
  );
}
